import { create } from "zustand";
import { getOrderData, OrderData } from "services/api";

export interface IUrlDataInit {
  data: {
    payment_id: string;
    amount: number;
    bank_name: string;
    bank_card_number: string | null;
    phone_number: string;
    full_name: string;
    transfer_method: string;
    status_name: string;
    geo_code: string;
    expires_in: number;
    bic?: string | null;
    account_number?: string | null;
    // Server API fields
    currency?: string;
    address?: string;
    expiresAt?: string;
    qrData?: string;
  },
}

interface IUrlData extends IUrlDataInit {
  loading: boolean;
  error: string | null;
  setData: (data: Partial<IUrlDataInit['data']>) => void;
  setMockData: (data: Partial<IUrlDataInit['data']>) => void;
  fetchOrderData: (orderId: string) => Promise<void>;
  mapServerData: (data: OrderData) => void;
}

export const initialState: IUrlDataInit = {
  data: {
    geo_code: '',
    transfer_method: '',
    amount: 0,
    bank_name: '',
    bank_card_number: null,
    phone_number: '',
    full_name: '',
    payment_id: '',
    status_name: '',
    expires_in: 0,
    bic: null,
    account_number: null,
    currency: '',
    address: '',
    expiresAt: '',
    qrData: '',
  },
};

const useUrlData = create<IUrlData>((set, get) => ({
  data: initialState.data,
  loading: false,
  error: null,

  setData: (newData) => set((state) => ({
    data: {
      ...state.data,
      ...newData,
    },
  })),

  // Set mock data for testing
  setMockData: (mockData) => set((state) => ({
    data: {
      ...initialState.data,
      ...mockData,
    },
    loading: false,
    error: null,
  })),

  // Map server API response to store format
  mapServerData: (serverData: OrderData) => {
    const expiresAt = new Date(serverData.expiresAt);
    const now = new Date();
    const expiresIn = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));

    set((state) => ({
      data: {
        ...state.data,
        payment_id: serverData.orderId,
        amount: serverData.amount,
        bank_name: serverData.bankName,
        full_name: serverData.recipient,
        status_name: serverData.state,
        transfer_method: serverData.method,
        expires_in: expiresIn,
        currency: serverData.currency,
        address: serverData.address,
        expiresAt: serverData.expiresAt,
        qrData: serverData.qrData || '',
      },
    }));
  },

  // Fetch order data from server
  fetchOrderData: async (orderId: string) => {
    set({ loading: true, error: null });
    try {
      const data = await getOrderData(orderId);
      get().mapServerData(data);
      set({ loading: false });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load order';
      set({ loading: false, error: errorMessage });
      throw error;
    }
  },
}));

export default useUrlData;